import React from 'react'
import styled, { keyframes } from 'styled-components'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import { wshColors } from './wshComponents'

const gentleGlow = keyframes`
  0%, 100% {
    box-shadow:
      0 -4px 20px rgba(0, 166, 118, 0.3),
      0 -2px 10px rgba(0, 166, 118, 0.15);
  }
  50% {
    box-shadow:
      0 -4px 32px rgba(0, 166, 118, 0.5),
      0 -2px 16px rgba(0, 166, 118, 0.25);
  }
`

const StyledFooter = styled.footer`
  background: linear-gradient(135deg, ${wshColors.primaryDark}, ${wshColors.primary}, ${wshColors.primaryLight});
  border-top: 2px solid ${wshColors.accent};
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  width: 100%;
  text-align: center;
  position: relative;
  animation: ${gentleGlow} 4s ease-in-out infinite;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg,
      transparent 0%,
      ${wshColors.accent} 20%,
      ${wshColors.accentLight} 50%,
      ${wshColors.accent} 80%,
      transparent 100%
    );
    box-shadow:
      0 0 15px ${wshColors.accent},
      0 0 30px rgba(0, 166, 118, 0.3);
  }
`

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 1.5rem;

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
  }
`

const FooterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`

const CopyrightText = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.3px;

  @media (max-width: 576px) {
    font-size: 0.8rem;
  }
`

const Separator = styled.span`
  color: ${wshColors.accent};
  font-weight: bold;
  text-shadow: 0 0 8px rgba(0, 166, 118, 0.5);
  margin: 0 0.5rem;

  @media (max-width: 576px) {
    display: none;
  }
`

const PoweredBy = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  min-width: 0;

  @media (max-width: 320px) {
    flex-direction: column;
    gap: 0.5rem;
    white-space: normal;
  }
`

const PoweredByLink = styled.a`
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, ${wshColors.accent}, ${wshColors.accentLight});
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${wshColors.accentLight};
    text-shadow: 0 0 8px rgba(0, 166, 118, 0.5);
    &::after { width: 100%; }
  }

  @media (max-width: 576px) {
    font-size: 0.85rem;
  }
`

const FooterLogo = styled.img`
  width: 45px;
  height: 45px;
  border-radius: 0.5rem;
  vertical-align: middle;
  display: inline-block;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1) translateY(-2px);
  }
`

const WshFooter: React.FC = () => {
  const { data: s } = usePublicSettings()
  const year = new Date().getFullYear()

  return (
    <StyledFooter>
      <FooterContainer>
        <FooterContent>
          <CopyrightText>
            Copyright © {year} {s?.company_name || 'Wholesale Hub'} - All Rights Reserved
          </CopyrightText>
          <Separator>&middot;</Separator>
          <PoweredBy>
            <PoweredByLink href="https://wholesalehub.dev" target="_blank" rel="noopener noreferrer">
              Powered by WholesaleHub
            </PoweredByLink>
            <PoweredByLink href="https://wholesalehub.dev" target="_blank" rel="noopener noreferrer">
              <FooterLogo src="/wsh/wsh_logo_grn.png" alt="WholesaleHub Logo" />
            </PoweredByLink>
          </PoweredBy>
        </FooterContent>
      </FooterContainer>
    </StyledFooter>
  )
}

export default WshFooter
